import { showSuccess } from '@calendly/alert';
import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CalendarIcon, CloseIcon, TimeClockCircleIcon } from '@calendly/ui/components/icons';
import { Modal } from '@calendly/ui/components/modal';
import { Sheet } from '@calendly/ui/components/sheet';
import { TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { textColorL2 } from '@calendly/ui/theme';
import classNames from 'classnames';
import { partition } from 'lodash-es';
import moment from 'moment-timezone';
import { useContext, useRef, useEffect, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { historyPush } from 'actions/core/navigation';
import { dateNotationSelector, timeNotationSelector, timezoneSelector } from 'selectors/current_user';
import { formatTimeRange, userDateFormatCheckYear } from 'services/time_format';
import { AddInvitee } from './add_invitee';
import { InviteesList } from './invitees_list';
import { Location } from './location';
import { WarningBanner } from './warning_banner';
import { MeetingDetailDependenciesContext } from '../../../../meeting_detail_context';
const containerIsPhoneCss = "c1kn855y";
const containerCss = "c1xg8ra1";
const Container = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Sheet ref={ref} {...props} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const scrollWrapperIsPhoneCss = "s1j3j8r4";
const scrollWrapperCss = "s1bnwn5i";
const ScrollWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(scrollWrapperCss, {
    [scrollWrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const headerCss = "h1f533ch";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const titleWrapperIsPhoneCss = "t12gfu5j";
const titleWrapperCss = "t4a6e4";
const TitleWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(titleWrapperCss, {
    [titleWrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const footerCss = "fd28evo";
const Footer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(footerCss, props.className)} />;
});
const detailsIsPhoneCss = "d70ddso";
const detailsCss = "d75zh3v";
const Details = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(detailsCss, {
    [detailsIsPhoneCss]: isPhone
  }, props.className)} />;
});
const inviteesCountCss = "i18cigpy";
const InviteesCount = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(inviteesCountCss, props.className)} />;
});
const cancelButtonCss = "cdnzv0n";
const CancelButton = forwardRef((props, ref) => {
  const decoration = useDeviceType('isPhone') ? 'secondary-outline' : 'ghost';
  return <Button ref={ref} {...props} decoration={decoration} className={classNames(cancelButtonCss, props.className)} />;
});
export const BookingModal = ({
  event,
  selectedSpot,
  onClose
}) => {
  const {
    name,
    eventTypeId,
    locationConfiguration
  } = event;
  const {
    invitees: defaultInvitees,
    startTime,
    endTime,
    hasExternalEventConflict,
    internalConflictEvents
  } = selectedSpot || {};
  const [allInvitees, setAllInvitess] = useState(defaultInvitees || []);
  const confirmationButtonRef = useRef();
  const dateNotation = useSelector(dateNotationSelector);
  const timeNotation = useSelector(timeNotationSelector);
  const timezone = useSelector(timezoneSelector);
  const dispatch = useDispatch();
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies?.useBookPollMutation) {
    throw new Error('BookingModal must be wrapped in a MeetingDetailDependenciesProvider with useBookPollMutation');
  }
  const [bookPoll, {
    isLoading,
    isSuccess,
    data
  }] = dependencies.useBookPollMutation();
  const date = userDateFormatCheckYear(moment(startTime), dateNotation);
  const timeRange = formatTimeRange(moment(startTime).tz(timezone), moment(endTime).tz(timezone), timeNotation);
  useOnValueChange(defaultInvitees, () => {
    setAllInvitess([...(defaultInvitees || [])]);
  });
  useOnValueChange(isSuccess, () => {
    if (isSuccess) {
      dispatch(showSuccess(I18n.t('publisher.flash.event_scheduled')));
      onClose();
      dispatch(historyPush(`/app/meetings/user/me?selected=${data.event.uuid}`));
    }
  });
  const removeInvitee = invitee => {
    if (invitee.id) {
      setAllInvitess(all => all.filter(({
        id
      }) => invitee.id !== id));
    } else {
      setAllInvitess(all => all.filter(({
        email
      }) => invitee.email !== email));
    }
  };
  const [invitees, additionalInvitees] = partition(allInvitees, ({
    id
  }) => id);
  const scheduleEvent = async () => {
    bookPoll({
      eventTypeId,
      inviteeIds: invitees.map(({
        id
      }) => id),
      startTime,
      locationConfiguration,
      additionalInvitees
    });
  };
  useEffect(() => {
    if (selectedSpot) {
      confirmationButtonRef.current.focus();
    }
  }, [selectedSpot]);
  if (!selectedSpot) return null;
  return <Modal isOpen={selectedSpot} onClose={onClose}>
      <Container data-component="booking-modal" data-testid="booking-modal">
        <ScrollWrapper>
          <Header>
            <TitleWrapper>
              <TitleText level={2} as="h1" size="medium">
                {I18n.t('publisher.contacts.buttons.book_meeting')}
              </TitleText>
            </TitleWrapper>
            <IconButton title={I18n.t('booking.button.close')} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Header>
          <div>
            <TitleText level={2} size="large">
              {name}
            </TitleText>
            <Details>
              <div>
                <CalendarIcon size="large" spacingRight={8} color={textColorL2} />
                {date}
              </div>
              <div>
                <TimeClockCircleIcon size="large" spacingRight={8} color={textColorL2} />
                {timeRange}
              </div>
              <Location locationConfiguration={locationConfiguration} />
            </Details>
          </div>
          <WarningBanner hasExternalEventConflict={hasExternalEventConflict} internalConflictEvents={internalConflictEvents} />
          <div>
            <InviteesCount>
              {I18n.t('publisher.scheduled_events.pending.booking_modal.invitees_count', {
              count: allInvitees.length
            })}
            </InviteesCount>
            <p>
              {I18n.t('publisher.scheduled_events.pending.booking_modal.invitees_notice')}
            </p>
            <AddInvitee allInvitees={allInvitees} addInvitee={newInvitee => setAllInvitess(all => [...all, newInvitee])} />
            <InviteesList invitees={invitees} additionalInvitees={additionalInvitees} onClickRemove={removeInvitee} />
          </div>
        </ScrollWrapper>
        <Footer>
          <CancelButton onClick={onClose}>
            {I18n.t('publisher.action.cancel')}
          </CancelButton>
          <Button onClick={scheduleEvent} loading={isLoading} ref={confirmationButtonRef}>
            {I18n.t('publisher.scheduled_events.pending.booking_modal.book_meeting')}
          </Button>
        </Footer>
      </Container>
    </Modal>;
};

require("./booking_modal.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./booking_modal.js");